package org.vreo.devicehub.features.device.presentation.deviceManagement.messages

import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.onStart
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlin.time.ExperimentalTime

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope

import org.vreo.devicehub.features.core.domain.DataError
import org.vreo.devicehub.features.core.domain.onError
import org.vreo.devicehub.features.core.domain.onSuccess
import org.vreo.devicehub.features.core.presentation.toUiText
import org.vreo.devicehub.features.core.domain.device.device.AnprDevice
import org.vreo.devicehub.features.core.domain.device.device.DeviceAuthentication
import org.vreo.devicehub.features.device.domain.DeviceAccessRepository
import org.vreo.devicehub.features.core.domain.device.message.AnprCameraMessage

/**
 * Represents the state of the Message List screen and handles all user actions.
 *
 * @param authentication target device authentication
 * @param repository device access repository for accessing remote device date
 */
class MessageListViewModel(
    private val authentication: DeviceAuthentication,
    private val repository: DeviceAccessRepository
) : ViewModel() {
    private val _state = MutableStateFlow(MessageListState(
        isDeviceActive = authentication.isDeviceActive,
        deviceName = authentication.deviceId
    ))
    val state = _state
        .onStart {
            updateMessageList()
        }
        .stateIn(
            scope = viewModelScope,
            started = SharingStarted.WhileSubscribed(5000L),
            initialValue = _state.value
        )

    private val messagesMaxLimit = 20
    private val messagesLoadLimit = 30

    /**
     * Comparator for messages. Sorts by date, newest first.
     */
    val messageComparator = compareByDescending<AnprCameraMessage> { it.date }

    /**
     * Handel all user actions
     */
    fun onAction(action: MessageListActions) {
        when (action) {
            is MessageListActions.OnClearError -> {
                _state.update {
                    it.copy(
                        error = null
                    )
                }
            }

            is MessageListActions.OnUpdateMessageList -> {
                updateMessageList(action.loadNew)
            }

            is MessageListActions.OnMessageClick -> {
                selectMessage(action.message)
            }
        }
    }

    /**
     * Updates the message list for the current device.
     *
     * @param loadNew Whether to load new messages or old messages.
     */
    @OptIn(ExperimentalTime::class)
    private fun updateMessageList(loadNew: Boolean = true) = viewModelScope.launch {
        // Check if device is active, if not display error
        if (!_state.value.isDeviceActive) {
            _state.update {
                it.copy(
                    isLoading = false,
                    error = DataError.Remote.SERVICE_UNAVAILABLE.toUiText()
                )
            }
            return@launch
        }

        val ignoreMsgIds = mutableListOf<String>()
        var fromTime: Long? = null
        var toTime: Long? = null

        // if message list is empty, get the newest messages.
        // if message list is not empty, and load new is true, get the newest messages if available.
        // if message list is not empty, and load new is false, get the old messages
        if (!_state.value.messages.isEmpty()) {
            when (loadNew) {
                true -> {
                    // Get the message ids to ignore.
                    _state.value.messages
                        .take(messagesMaxLimit)
                        .mapTo(ignoreMsgIds) { it.msgId }
                    fromTime = _state.value.messages.first().timeStamp
                }

                false -> {
                    // Get the message ids to ignore
                    _state.value.messages
                        .takeLast(messagesMaxLimit)
                        .mapTo(ignoreMsgIds) { it.msgId }

                    toTime = _state.value.messages.last().timeStamp
                }
            }
        }

        runCatching {
            repository.getMessages(
                token = authentication.userToken,
                deviceId = authentication.deviceId,
                limit = messagesLoadLimit,
                fromTime = fromTime,
                toTime = toTime,
                ignoreIds = ignoreMsgIds
            ).onSuccess {
                updateMessages(it)
            }.onError { error ->
                _state.update {
                    it.copy(
                        isLoading = false,
                        error = error.toUiText()

                    )
                }
            }
        }.getOrElse {
            _state.update {
                it.copy(
                    error = DataError.Remote.UNKNOWN.toUiText()
                )
            }
        }
    }

    /**
     * Updates the messages in the current state. Adds only new messages
     * that are not already present (based on msgId), then sorts them by date.
     *
     * @param messages List of messages to update
     */
    private fun updateMessages(messages: List<AnprCameraMessage>) {
        try {
            val existingIds = _state.value.messages.mapTo(hashSetOf()) { it.msgId }
            val newMessages = messages.filterNot { it.msgId in existingIds }

            if (newMessages.isNotEmpty()) {
                _state.update { it.copy(isLoading = true) }

                val updatedMessages = (_state.value.messages + newMessages)
                    .sortedWith(messageComparator)

                _state.update {
                    it.copy(
                        isLoading = false,
                        messages = updatedMessages,
                    )
                }
            }
        } catch (_: Exception) {
            _state.update {
                it.copy(
                    isLoading = false,
                    error = DataError.Remote.UNKNOWN.toUiText(),
                )
            }
        }
    }

    /**
     * Selects a message and updates the state accordingly.
     * Ensures only one message is marked as selected at a time.
     *
     * @param message The message to select.
     */
    private fun selectMessage(message: AnprCameraMessage) = viewModelScope.launch {
        runCatching {
            val currentlySelectedId = _state.value.selectedMessage?.id

            if (currentlySelectedId == message.id) return@launch

            // Iterate through the messages and update set selected flag for newly select
            // message and unselect selected flag for previously selected message
            val updatedMessages = _state.value.messages.map {
                when (it.id) {
                    message.id -> it.copy(selected = true)
                    currentlySelectedId -> it.copy(selected = false)
                    else -> it
                }
            }

            _state.update {
                it.copy(
                    messages = updatedMessages,
                    selectedMessage = message
                )
            }
            updateImage(message)
        }.getOrElse {
            _state.update {
                it.copy(
                    error = DataError.Remote.UNKNOWN.toUiText()
                )
            }
        }
    }

    /**
     * Updates the image for the selected message.
     *
     * @param message The message for which to update the image.
     */
    private fun updateImage(message: AnprCameraMessage) = viewModelScope.launch {
        // Use existing image if available
        if (message.image != null) {
            _state.update {
                it.copy(
                    image = message.image,
                )
            }
            return@launch
        }

        // Acquire new image from server
        runCatching {
            repository.getImage(
                token = authentication.userToken,
                path = message.imageUrl
            )
                .onSuccess { image ->
                    // Cache image, locate selected message and update it with new image
                    val updatedMessageList = _state.value.messages.map {
                        if (it.id == message.id) {
                            it.copy(image = image)
                        } else {
                            it
                        }
                    }

                    // Display image
                    _state.update {
                        it.copy(
                            image = image,
                            messages = updatedMessageList
                        )
                    }
                }.onError { error ->
                    _state.update {
                        it.copy(
                            error = error.toUiText()
                        )
                    }
                }
        }.getOrElse {
            _state.update {
                it.copy(
                    error = DataError.Remote.UNKNOWN.toUiText(),
                )
            }
        }
    }
}